import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Button,
  Input,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getRooms } from "../../components/Api/GmsRoom";
import { getBookings, saveBooking, checkAvailability, checkAvailabilityForTime, updateBulkCompletionStatus, exportBookingsToExcel, exportBookingDetailsToExcel } from "../../components/Api/GmsBooking";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getSpaPackages, getSpaPackageById } from "../../components/Api/GmsSpaPackage";
import { getSpaPackageSeries } from "../../components/Api/GmsSpaPackageSeries";
import { getCustomerMemberships } from "../../components/Api/GmsCustomerMembership";
import { getEquipments } from "../../components/Api/GmsEquipment";
import { getSalesInvoiceById } from "../../components/Api/GmsBilling";
import { Calendar, Clock, User, MapPin, Edit, X, Eye, Receipt, Download } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import QuickAddCustomer from "../../components/QuickAddCustomer";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";

// Add CSS for loading spinner
const spinnerStyle = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Helper: format local date/time without timezone (no trailing 'Z')
const formatLocalDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatLocalDateTime = (date) => {
  if (!date) return null;
  const ymd = formatLocalDate(date);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${ymd}T${hh}:${mm}:${ss}`;
};

// Helper: check if all services in a booking are complete
const areAllServicesComplete = (booking) => {
  if (!booking.details || booking.details.length === 0) {
    return false;
  }
  
  for (const detail of booking.details) {
    if (!detail.resourceDetails || detail.resourceDetails.length === 0) {
      return false;
    }
    
    for (const service of detail.resourceDetails) {
      if (service.completionStatus !== 'Complete') {
        return false;
      }
    }
  }
  
  return true;
};

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  booking_Id: 0,
  customer_Id: null,
  employee_Id: null,
  room_Id: null,
  from_Time: new Date(),
  to_Time: new Date(),
  entry_Date: new Date(),
  booking_Date: new Date(),
  is_Cancelled: false,
  cancellation_Reason: "",
  notes: "",
  source: null, // 👈 NEW FIELD
  sourceOther: "", // 👈 NEW FIELD FOR "OTHERS" INPUT
  modified_By: null,
  modified_On: null,
  // New fields for selected items
  selectedServices: [],
  selectedPackages: [],
  selectedPackageSeries: [],
};

const emptyFilter = {
  booking_Id: null,
  customer_Id: null,
  employee_Id: null,
  room_Id: null,
  from_Date: null,
  to_Date: null,
};

const BookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("1");
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]); // Store full dataset
  const [customers, setCustomers] = useState([]);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [selectedCustomerFilterOption, setSelectedCustomerFilterOption] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [equipments, setEquipments] = useState([]);
  // New state for services, packages, and package series
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageSeries, setPackageSeries] = useState([]);
  // State for service resource allocation
  const [serviceResources, setServiceResources] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Membership-related state
  const [customerMembership, setCustomerMembership] = useState(null);
  const [hasMembership, setHasMembership] = useState(false);
  const [membershipServices, setMembershipServices] = useState([]);
  const [membershipPackages, setMembershipPackages] = useState([]);
  const [membershipPackageSeries, setMembershipPackageSeries] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [formKey, setFormKey] = useState(0);
  const [filterKey, setFilterKey] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  
  // New state for availability checking
  const [availabilityResults, setAvailabilityResults] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  
  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsBooking');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsBooking');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsBooking');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsBooking');
  const isAdmin = isUserAdmin();
  
  // Get current user's employee ID for filtering
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentEmployeeId = currentUser.user_EmployeeId || currentUser.employeeId;
  
  // New state for time-based availability
  const [timeAvailabilityResults, setTimeAvailabilityResults] = useState({});
  const [checkingTimeAvailability, setCheckingTimeAvailability] = useState(false);

  // New state for cancellation modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancellingBookingId, setCancellingBookingId] = useState(null);

  // New state for entity cancellation modal
  const [showEntityCancelModal, setShowEntityCancelModal] = useState(false);
  const [entityCancelReason, setEntityCancelReason] = useState("");
  const [cancellingEntity, setCancellingEntity] = useState({ type: '', index: -1, name: '' });

  // New state for membership popup modal
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [selectedMembershipServices, setSelectedMembershipServices] = useState([]);
  const [selectedMembershipPackages, setSelectedMembershipPackages] = useState([]);
  const [selectedMembershipPackageSeries, setSelectedMembershipPackageSeries] = useState([]);
  
  // New state for hierarchical selection
  const [hierarchicalSelection, setHierarchicalSelection] = useState({
    packageSeries: {},
    packages: {},
    services: {}
  });

  // Quick Add Customer state
  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);

  // Source dropdown options
  const sourceOptions = [
    { value: 'new', label: 'New' },
    { value: 'repeat', label: 'Repeat' },
    { value: 'others', label: 'Others' }
  ];

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
      
      // Set filter visibility based on permissions
      if (hasEditOrHigher) {
        setShowFilters(true); // Show filters by default for users with edit permission
      } else {
        setShowFilters(false); // Hide filters for users without edit permission
      }
      
      // Check if there's a date parameter in the URL
      const urlParams = new URLSearchParams(location.search);
      const dateParam = urlParams.get('date');
      
      if (dateParam) {
        try {
          const selectedDate = new Date(dateParam);
          if (!isNaN(selectedDate.getTime())) {
            setForm(prev => ({
              ...prev,
              booking_Date: selectedDate
            }));
          }
        } catch (error) {
          console.error('Error parsing date from URL:', error);
        }
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly, hasEditOrHigher, location.search]);

  useEffect(() => {
    if (activeTab === "2") {
      loadBookings();
    }
  }, [activeTab]); // Remove page and pageSize dependencies since we're doing client-side pagination

  // Handle navigation from notification
  useEffect(() => {
    if (location.state?.bookingId && location.state?.editMode) {
      const bookingId = location.state.bookingId;
      const booking = bookings.find(b => b.Booking_Id === bookingId);
      if (booking) {
        handleEdit(booking);
      }
    }
  }, [location.state, bookings]);

  // Handle client-side pagination when page changes
  useEffect(() => {
    if (activeTab === "2" && allBookings.length > 0) {
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = allBookings.slice(startIndex, endIndex);
      setBookings(paginatedData);
    }
  }, [page, pageSize, allBookings]);

  // Reset page to 1 when filters change
  useEffect(() => {
    if (activeTab === "2") {
      setPage(1);
    }
  }, [filter]);

  // Check availability when service resources change
  useEffect(() => {
    if (serviceResources.length > 0 && form.booking_Date) {
      // Check time-based availability for each resource that has time set
      serviceResources.forEach(resource => {
        if (resource.fromTime && resource.toTime) {
          console.log('Auto-checking availability for resource:', resource.id);
          checkTimeAvailability(resource.id, resource.fromTime, resource.toTime);
        }
      });
    }
  }, [serviceResources, form.booking_Date]);

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadCustomers(),
        loadEmployees(),
        loadRooms(),
        loadEquipments(),
        loadServices(),
        loadPackages(),
        loadPackageSeries(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
      Swal.fire("Error", "Failed to load initial data", "error");
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers({});
      const formattedCustomers = data.map(customer => ({
        value: customer.customer_Id,
        label: customer.customer_NameEn || customer.customer_Name,
      }));
      setCustomers(formattedCustomers);
    } catch (error) {
      console.error('Error loading customers:', error);
      Swal.fire("Error", "Failed to load customers", "error");
    }
  };

  // Manual search function for customer search
  const searchCustomersManually = async (inputValue) => {
    console.log('🔍 searchCustomersManually called with inputValue:', inputValue);
    
    if (!inputValue || inputValue.length < 3) {
      console.log('❌ Input too short, clearing options');
      setCustomerSearchOptions([]);
      return;
    }
    
    try {
      setCustomerSearchLoading(true);
      console.log('🚀 Making API call to searchCustomers with:', { inputValue, page: 1, pageSize: 20 });
      const response = await searchCustomers(inputValue, 1, 20);
      console.log('✅ API response received:', response);
      
      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));
      
      console.log('📋 Formatted options:', formattedOptions);
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  // Manual search function for customer filter search
  const searchCustomersFilterManually = async (inputValue) => {
    console.log('🔍 searchCustomersFilterManually called with inputValue:', inputValue);
    
    if (!inputValue || inputValue.length < 3) {
      console.log('❌ Input too short, clearing options');
      setCustomerSearchOptions([]);
      return;
    }
    
    try {
      setCustomerSearchLoading(true);
      console.log('🚀 Making API call to searchCustomers with:', { inputValue, page: 1, pageSize: 20 });
      const response = await searchCustomers(inputValue, 1, 20);
      console.log('✅ API response received:', response);
      
      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));
      
      console.log('📋 Formatted options:', formattedOptions);
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const handleCustomerAdded = async (newCustomer) => {
    console.log("🔍 BookingPage - handleCustomerAdded received:", newCustomer);
    
    // Get the customer ID from the response
    const customerId = newCustomer.customer_Id || newCustomer.id;
    console.log("🔍 Looking for customer ID:", customerId);
    
    try {
      // Try to fetch the specific customer by ID
      const customerData = await getCustomers({ customer_Id: customerId });
      console.log("🔍 Fetched customer data:", customerData);
      
      if (customerData && customerData.length > 0) {
        const addedCustomer = customerData[0];
        console.log("🔍 Found customer:", addedCustomer);
        
        // Set the newly added customer as selected
        setForm(prev => ({
          ...prev,
          customer_Id: customerId
        }));
        // Set the selected customer option for display
        setSelectedCustomerOption({
          value: customerId,
          label: `${addedCustomer.customer_NameEn || 'N/A'} — ${addedCustomer.customer_ContactNo || 'N/A'}`
        });
        console.log("🔍 Set customer option:", `${addedCustomer.customer_NameEn || 'N/A'} — ${addedCustomer.customer_ContactNo || 'N/A'}`);
        
        // Also reload the full customer list to keep it updated
        loadCustomers();
      } else {
        // Fallback: just set the ID and let the user search for the customer
        setForm(prev => ({
          ...prev,
          customer_Id: customerId
        }));
        setSelectedCustomerOption({
          value: customerId,
          label: `Customer ID: ${customerId}`
        });
        console.log("🔍 Customer not found, using fallback");
        
        // Still reload the full customer list
        loadCustomers();
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      // Fallback: just set the ID
      setForm(prev => ({
        ...prev,
        customer_Id: customerId
      }));
      setSelectedCustomerOption({
        value: customerId,
        label: `Customer ID: ${customerId}`
      });
      console.log("🔍 Error occurred, using fallback");
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await getEmployees({});
      console.log('Employees API response:', data);
      const formattedEmployees = data.map(emp => ({
        value: emp.employee_Id,
        label: emp.employee_NameEn || emp.employee_Name,
      }));
      console.log('Formatted employees:', formattedEmployees);
      setEmployees(formattedEmployees);
    } catch (error) {
      console.error('Error loading employees:', error);
      Swal.fire("Error", "Failed to load employees", "error");
    }
  };

  const loadRooms = async () => {
    try {
      const data = await getRooms({});
      console.log('Rooms API response:', data);
      const formattedRooms = data.map(room => ({
        value: room.room_Id,
        label: room.room_Name_English || room.room_Name_Arabic || room.room_Name,
      }));
      console.log('Formatted rooms:', formattedRooms);
      setRooms(formattedRooms);
    } catch (error) {
      console.error('Error loading rooms:', error);
      Swal.fire("Error", "Failed to load rooms", "error");
    }
  };

  // New load functions for services, packages, and package series
  const loadServices = async () => {
    try {
      const data = await getSpaServices({ status: true });
      console.log('Services API response:', data);
      const formattedServices = data.map(service => ({
        value: service.spaService_Id,
        label: service.spaServiceName_En || service.spaServiceName_Ar || service.spaService_NameEn || service.spaService_Name,
        duration: service.duration || 30, // Include duration field from API response
      }));
      console.log('Formatted services:', formattedServices);
      setServices(formattedServices);
    } catch (error) {
      console.error('Error loading services:', error);
      Swal.fire("Error", "Failed to load services", "error");
    }
  };

  const loadPackages = async () => {
    try {
      const data = await getSpaPackages({ status: true });
      console.log('Packages API response:', data);
      const formattedPackages = data.map(pkg => ({
        value: pkg.packageId,
        label: pkg.packageName_En || pkg.packageName_Ar || pkg.package_NameEn || pkg.package_Name,
      }));
      console.log('Formatted packages:', formattedPackages);
      setPackages(formattedPackages);
    } catch (error) {
      console.error('Error loading packages:', error);
      Swal.fire("Error", "Failed to load packages", "error");
    }
  };

  const loadPackageSeries = async () => {
    try {
      const data = await getSpaPackageSeries({ status: true });
      const formattedPackageSeries = data.map(series => ({
        value: series.seriesId,
        label: series.seriesName_En || series.seriesName_Ar,
      }));
      setPackageSeries(formattedPackageSeries);
    } catch (error) {
      console.error('Error loading package series:', error);
      Swal.fire("Error", "Failed to load package series", "error");
    }
  };

  const loadEquipments = async () => {
    try {
      const data = await getEquipments({ status: true });
      const formattedEquipments = data.map(equipment => ({
        value: equipment.eq_Id,
        label: equipment.eq_Name_English || equipment.eq_Name_Arabic,
      }));
      setEquipments(formattedEquipments);
    } catch (error) {
      console.error('Error loading equipments:', error);
      Swal.fire("Error", "Failed to load equipments", "error");
    }
  };

  // Function to fetch services from selected packages
  const fetchPackageServices = async (packageIds) => {
    try {
      const allPackageServices = [];
      
      for (const packageId of packageIds) {
        const packageData = await getSpaPackageById(packageId);
        if (packageData && packageData.details) {
          packageData.details.forEach(detail => {
            const service = services.find(s => s.value === detail.service_Id);
            if (service) {
              allPackageServices.push({
                ...service,
                packageId: packageId,
                packageName: packageData.packageName_En || packageData.packageName_Ar,
                serviceId: detail.service_Id,
                quantity: detail.quantity || 1,
                duration: service.duration || 30 // Use service duration from loaded services
              });
            }
          });
        }
      }
      
      return allPackageServices;
    } catch (error) {
      console.error('Error fetching package services:', error);
      return [];
    }
  };

  // Helper function to get package series from membership
  const getPackageSeriesFromMembership = async (membershipId) => {
    try {
      const memberships = await getCustomerMemberships({});
      const membership = memberships.find(m => m.membershipId === membershipId);
      if (membership && membership.installments) {
        const uniqueSeriesIds = [...new Set(membership.installments.map(installment => installment.seriesId))];
        return uniqueSeriesIds.map(seriesId => {
          const series = packageSeries.find(s => s.value === seriesId);
          return series;
        }).filter(Boolean);
      }
      return [];
    } catch (error) {
      console.error('Error getting package series from membership:', error);
      return [];
    }
  };

  // Check customer membership and populate dropdowns accordingly
  const checkCustomerMembership = async (customerId) => {
    if (!customerId) {
      // Reset membership data when no customer is selected
      setCustomerMembership(null);
      setHasMembership(false);
      setMembershipServices([]);
      setMembershipPackages([]);
      setMembershipPackageSeries([]);
      return;
    }

    try {
      const memberships = await getCustomerMemberships({ customerId: customerId });
      
      if (memberships && memberships.length > 0) {
        // Customer has membership
        const membership = memberships[0]; // Use the first membership
        setCustomerMembership(membership);
        setHasMembership(true);
        
        // Filter only pending services from membership details
        const pendingDetails = membership.details.filter(detail => detail.completionStatus === "Pending");
        
        // Get unique service IDs from pending details
        const uniqueServiceIds = [...new Set(pendingDetails.map(detail => detail.serviceId))];
        const uniquePackageIds = [...new Set(pendingDetails.map(detail => detail.packageId))];
        const uniqueSeriesIds = [...new Set(membership.installments.map(installment => installment.seriesId))];
        
        // Populate membership-specific dropdowns
        const membershipServicesData = [];
        const membershipPackagesData = [];
        const membershipPackageSeriesData = [];
        
        // Get service names from the main services list
        uniqueServiceIds.forEach(serviceId => {
          const service = services.find(s => s.value === serviceId);
          if (service) {
            membershipServicesData.push(service);
          }
        });
        
        // Get package names from the main packages list
        uniquePackageIds.forEach(packageId => {
          const pkg = packages.find(p => p.value === packageId);
          if (pkg) {
            membershipPackagesData.push(pkg);
          }
        });
        
        // Get package series names from the main package series list
        uniqueSeriesIds.forEach(seriesId => {
          const series = packageSeries.find(s => s.value === seriesId);
          if (series) {
            membershipPackageSeriesData.push(series);
          }
        });
        
        setMembershipServices(membershipServicesData);
        setMembershipPackages(membershipPackagesData);
        setMembershipPackageSeries(membershipPackageSeriesData);
        
      } else {
        // Customer has no membership
        setCustomerMembership(null);
        setHasMembership(false);
        setMembershipServices([]);
        setMembershipPackages([]);
        setMembershipPackageSeries([]);
      }
    } catch (error) {
      console.error('Error checking customer membership:', error);
      // On error, assume no membership
      setCustomerMembership(null);
      setHasMembership(false);
      setMembershipServices([]);
      setMembershipPackages([]);
      setMembershipPackageSeries([]);
    }
  };

  // Check availability for specific time slot
  const checkTimeAvailability = async (resourceId, fromTime, toTime) => {
    if (!form.booking_Date || !fromTime || !toTime) {
      console.log('Skipping time availability check - missing data:', { form: form.booking_Date, fromTime, toTime });
      return;
    }

    try {
      setCheckingTimeAvailability(true);
      
      const bookingDate = new Date(form.booking_Date);
      const bookingDateStr = formatLocalDate(bookingDate);
      const fromTimeStr = formatLocalDateTime(fromTime);
      const toTimeStr = formatLocalDateTime(toTime);

      const timeData = {
        booking_Id: form.booking_Id > 0 ? form.booking_Id : null,
        booking_Date: bookingDateStr,
        from_Time: fromTimeStr,
        to_Time: toTimeStr
      };

      console.log('Time availability check data:', timeData);
      const results = await checkAvailabilityForTime(timeData);
      console.log('Time availability results:', results);
      
      // Store results for this specific resource
      setTimeAvailabilityResults(prev => ({
        ...prev,
        [resourceId]: results
      }));
      
      console.log('Updated time availability results for resource:', resourceId, results);
    } catch (error) {
      console.error('Error checking time availability:', error);
      // Don't show error to user as this is a background check
    } finally {
      setCheckingTimeAvailability(false);
    }
  };

  // Check availability for service resources
  const checkAvailabilityForSelection = async () => {
    if (!form.booking_Date || serviceResources.length === 0) {
      return;
    }

    try {
      setCheckingAvailability(true);
      
      const bookingDate = new Date(form.booking_Date);
      const bookingDateStr = formatLocalDate(bookingDate);

      // Create service resources for availability check
      const serviceResourcesForCheck = serviceResources
        .filter(resource => resource.employeeId || resource.roomId) // Only include resources with employee or room
        .map(resource => ({
          employee_Id: resource.employeeId || null,
          room_Id: resource.roomId || null,
          from_Time: resource.fromTime ? formatLocalDateTime(resource.fromTime) : null,
          to_Time: resource.toTime ? formatLocalDateTime(resource.toTime) : null
        }));

      // Don't make API call if no valid resources
      if (serviceResourcesForCheck.length === 0) {
        setAvailabilityResults({});
        return;
      }

      const availabilityData = {
        booking_Id: form.booking_Id > 0 ? form.booking_Id : null,
        booking_Date: bookingDateStr,
        serviceResources: serviceResourcesForCheck
      };

      console.log('Availability check data:', availabilityData);
      const results = await checkAvailability(availabilityData);
      console.log('Availability results:', results);
      
      // Convert results to a map for easy lookup
      const availabilityMap = {};
      results.forEach(result => {
        availabilityMap[`${result.type}_${result.id}`] = result;
      });
      
      setAvailabilityResults(availabilityMap);
    } catch (error) {
      console.error('Error checking availability:', error);
      // Don't show error to user as this is a background check
    } finally {
      setCheckingAvailability(false);
    }
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      
      // Format dates properly to avoid timezone issues
      const formatDateForAPI = (date) => {
        if (!date) return null;
        // Create date in local timezone and format as YYYY-MM-DD
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const filterData = {
        ...filter,
        from_Date: formatDateForAPI(filter.from_Date),
        to_Date: formatDateForAPI(filter.to_Date),
        page: page,
        pageSize: pageSize,
      };
      
      // For users without edit permission, filter by their employee ID only
      if (!hasEditOrHigher && currentEmployeeId) {
        filterData.employee_Id = currentEmployeeId;
      }
      
      const data = await getBookings(filterData);
      
      // Sort by booking_id in descending order (latest first)
      const sortedData = data.sort((a, b) => (b.booking_Id || 0) - (a.booking_Id || 0));
      
      // Store full dataset
      setAllBookings(sortedData);
      setTotalCount(sortedData.length);
      
      // Apply initial pagination
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = sortedData.slice(startIndex, endIndex);
      setBookings(paginatedData);
    } catch (error) {
      console.error("Error loading bookings:", error);
      Swal.fire("Error", "Failed to load bookings", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = async (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));

    // Check customer membership when customer is selected
    if (field === 'customer_Id') {
      if (value) {
      checkCustomerMembership(value);
      } else {
        // Clear form when customer is cleared
        setServiceResources([]);
        setForm(prev => ({
          ...prev,
          selectedServices: [],
          selectedPackages: [],
          selectedPackageSeries: []
        }));
        // Reset membership data
        setCustomerMembership(null);
        setHasMembership(false);
        setMembershipServices([]);
        setMembershipPackages([]);
        setMembershipPackageSeries([]);
      }
    }

    // Handle package selection - fetch services from selected packages
    if (field === 'selectedPackages') {
      if (value && value.length > 0) {
        const packageIds = value.map(pkg => pkg.value);
        const packageServices = await fetchPackageServices(packageIds);
        
        // Update service resources with new package services
        setServiceResources(prev => {
          // Remove existing non-membership package services
          const filtered = prev.filter(resource => !(resource.packageId && !resource.membershipId));
          // Add new package services with unique time slots
          const newResources = packageServices.map((service, index) => {
            // Create unique time slots for each service using service duration
            const baseTime = new Date();
            baseTime.setHours(9, 0, 0, 0); // Start at 9:00 AM
            
            // Calculate cumulative time for each service
            let cumulativeMinutes = 0;
            for (let i = 0; i < index; i++) {
              const prevService = packageServices[i];
              const prevDuration = prevService.duration || 30; // Use service duration from API
              cumulativeMinutes += prevDuration;
            }
            
            const fromTime = new Date(baseTime.getTime() + (cumulativeMinutes * 60000));
            const duration = service.duration || 30; // Use service duration from API
            const toTime = new Date(fromTime.getTime() + (duration * 60000));
            
            return {
              id: `pkg-${service.packageId}-${service.serviceId}-${Date.now()}-${Math.random()}-${index}`,
            serviceId: service.serviceId,
            serviceName: service.label,
            packageId: service.packageId,
            packageName: service.packageName,
            employeeId: null,
            roomId: null,
            equipmentId: null,
              fromTime: fromTime,
              toTime: toTime
            };
          });
          return [...filtered, ...newResources];
        });
      } else {
        // Remove non-membership package services when no packages selected
        setServiceResources(prev => prev.filter(resource => !(resource.packageId && !resource.membershipId)));
      }
    }

    // Handle individual service selection
    if (field === 'selectedServices') {
      if (value && value.length > 0) {
        // Update service resources with individual services
        setServiceResources(prev => {
          // Remove existing non-membership individual services
          const filtered = prev.filter(resource => !(!resource.packageId && !resource.membershipId));
          // Add new individual services with unique time slots
          const newResources = value.map((service, index) => {
            // Create unique time slots for each service using service duration
            const baseTime = new Date();
            baseTime.setHours(9, 0, 0, 0); // Start at 9:00 AM
            
            // Calculate cumulative time for each service
            let cumulativeMinutes = 0;
            for (let i = 0; i < index; i++) {
              const prevService = value[i];
              const prevDuration = prevService.duration || 30; // Use service duration from API
              cumulativeMinutes += prevDuration;
            }
            
            const fromTime = new Date(baseTime.getTime() + (cumulativeMinutes * 60000));
            const duration = service.duration || 30; // Use service duration from API
            const toTime = new Date(fromTime.getTime() + (duration * 60000));
            
            return {
              id: `srv-${service.value}-${Date.now()}-${Math.random()}-${index}`,
            serviceId: service.value,
            serviceName: service.label,
            packageId: null,
            packageName: null,
            employeeId: null,
            roomId: null,
            equipmentId: null,
              fromTime: fromTime,
              toTime: toTime
            };
          });
          return [...filtered, ...newResources];
        });
      } else {
        // Remove non-membership individual services when no services selected
        setServiceResources(prev => prev.filter(resource => !(!resource.packageId && !resource.membershipId)));
      }
    }

    // Check availability when booking date changes
    if (field === 'booking_Date') {
      // Use setTimeout to avoid too many API calls while user is typing
      setTimeout(() => {
        checkAvailabilityForSelection();
      }, 500);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle resource allocation changes for services
  const handleResourceChange = (resourceId, field, value) => {
    setServiceResources(prev => 
      prev.map(resource => 
        resource.id === resourceId 
          ? { ...resource, [field]: value || null }
          : resource
      )
    );
    
    // Check time-based availability when time changes
    if (['fromTime', 'toTime'].includes(field)) {
      // Get the updated resource after the change
      const updatedResource = serviceResources.find(r => r.id === resourceId);
      if (updatedResource && updatedResource.fromTime && updatedResource.toTime) {
        console.log('Triggering time availability check for resource:', resourceId, 'from:', updatedResource.fromTime, 'to:', updatedResource.toTime);
        setTimeout(() => {
          checkTimeAvailability(resourceId, updatedResource.fromTime, updatedResource.toTime);
        }, 500);
      }
    }
    
    // Check availability when employee or room changes
    if (['employeeId', 'roomId'].includes(field)) {
      setTimeout(() => {
        checkAvailabilityForSelection();
      }, 500);
    }
  };

  // Helper function to format options with availability indicators
  const formatOptionsWithAvailability = (options, type, resourceId) => {
    const timeAvailability = timeAvailabilityResults[resourceId];
    
    console.log('Formatting options for:', { type, resourceId, timeAvailability });
    
    return options.map(option => {
      let isAvailable = true;
      
      if (timeAvailability) {
        if (type === 'Employee') {
          const employee = timeAvailability.availableEmployees?.find(emp => emp.employee_Id === option.value);
          isAvailable = employee ? employee.isAvailable : true;
          console.log('Employee availability:', { employeeId: option.value, employee, isAvailable });
        } else if (type === 'Room') {
          const room = timeAvailability.availableRooms?.find(room => room.room_Id === option.value);
          isAvailable = room ? room.isAvailable : true;
          console.log('Room availability:', { roomId: option.value, room, isAvailable });
        }
      }
      
      return {
        ...option,
        isDisabled: !isAvailable, // Disable unavailable options
        label: (
          <div className="d-flex align-items-center">
            <span className={`me-2 ${isAvailable ? 'text-success' : 'text-danger'}`}>
              {isAvailable ? '✓' : '✗'}
            </span>
            <span>{option.label}</span>
          </div>
        )
      };
    });
  };

  // Handle service resource removal
  const removeServiceResource = (resourceId) => {
    setServiceResources(prev => {
      const resourceToRemove = prev.find(r => r.id === resourceId);
      if (!resourceToRemove) return prev;

      // If it's a package service (non-membership), remove all services from that package
      if (resourceToRemove.packageId && !resourceToRemove.membershipId) {
        // Also remove the package from the form's selectedPackages
        setForm(prevForm => ({
          ...prevForm,
          selectedPackages: prevForm.selectedPackages.filter(pkg => pkg.value !== resourceToRemove.packageId)
        }));
        
        return prev.filter(r => !(r.packageId === resourceToRemove.packageId && !r.membershipId));
      }

      // For individual services or membership package services, remove just this one
      return prev.filter(r => r.id !== resourceId);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.customer_Id || !form.source) {
      Swal.fire("Validation Error", "Please fill all required fields", "error");
      return;
    }

    // Validate source other field if "others" is selected
    if (form.source === 'others' && (!form.sourceOther || form.sourceOther.trim() === '')) {
      Swal.fire("Validation Error", "Please specify the source details", "error");
      return;
    }

    // Validate that we have services to book
    if (serviceResources.length === 0) {
      Swal.fire("Validation Error", "Please select at least one service or package", "error");
      return;
    }

    // Validate resource allocation for each service (only validate time logic if times are provided)
    for (const resource of serviceResources) {
      // Only validate time logic if both times are provided
      if (resource.fromTime && resource.toTime) {
        if (new Date(resource.toTime) <= new Date(resource.fromTime)) {
          Swal.fire("Validation Error", `To time must be after from time for service: ${resource.serviceName}`, "error");
          return;
        }
      }
    }

    try {
      setLoading(true);
      
      // Get current user ID from localStorage
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const currentUserId = authUser.userId || authUser.user_Id || 1;
      
      // Prepare booking details for selected items
      const details = [];
      const resourceDetails = [];
      let detailIndex = 1;

      // Group services by type (individual, package, membership)
      const individualServices = serviceResources.filter(r => !r.packageId && !r.membershipId);
      const packageServices = serviceResources.filter(r => r.packageId && !r.membershipId);
      const membershipServices = serviceResources.filter(r => r.membershipId);
      
      // Get unique packages from package services
      const uniquePackages = [...new Set(packageServices.map(r => r.packageId))];
      
      // Get unique memberships from membership services
      const uniqueMemberships = [...new Set(membershipServices.map(r => r.membershipId))];
      
      // Add individual services to details
      if (individualServices.length > 0) {
        individualServices.forEach(service => {
          const detailId = detailIndex++;
          details.push({
            tempDetailId: detailId,
            bookingDetail_Id: 0,
            entity_Id: service.serviceId,
            entity_Type: 'ser',
            is_Cancelled: false,
            cancel_Reason: null,
            created_By: currentUserId,
            created_On: new Date().toISOString(),
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
          });
          
          // Create local date-time without timezone conversion
          const bookingDate = new Date(form.booking_Date);
          const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), 
                                   service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
          const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), 
                                 service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

          // Add resource detail for individual service
          resourceDetails.push({
            tempDetailId: detailId,
            bookingResourceDetail_Id: 0,
            bookingDetail_Id: 0,
            service_Id: service.serviceId,
            package_Id: null,
            membership_Id: null,
            room_Id: service.roomId,
            equipment_Id: service.equipmentId,
            employee_Id: service.employeeId,
            from_Time: fromTime ? formatLocalDateTime(fromTime) : null,
            to_Time: toTime ? formatLocalDateTime(toTime) : null,
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
          });
        });
      }
      
      // Add packages to details
      if (uniquePackages.length > 0) {
        uniquePackages.forEach(packageId => {
          const detailId = detailIndex++;
          details.push({
            tempDetailId: detailId,
            bookingDetail_Id: 0,
            entity_Id: packageId,
            entity_Type: 'pac',
            is_Cancelled: false,
            cancel_Reason: null,
            created_By: currentUserId,
            created_On: new Date().toISOString(),
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
          });
          
          // Add resource details for all services in this package
          const servicesInPackage = packageServices.filter(r => r.packageId === packageId);
          servicesInPackage.forEach(service => {
            // Create local date-time without timezone conversion
            const bookingDate = new Date(form.booking_Date);
            const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), 
                                     service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
            const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), 
                                   service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

            resourceDetails.push({
              tempDetailId: detailId,
              bookingResourceDetail_Id: 0,
              bookingDetail_Id: 0,
              service_Id: service.serviceId,
              package_Id: packageId,
              membership_Id: null,
              room_Id: service.roomId,
              equipment_Id: service.equipmentId,
              employee_Id: service.employeeId,
              from_Time: formatLocalDateTime(fromTime),
              to_Time: formatLocalDateTime(toTime),
              modified_By: currentUserId,
              modified_On: new Date().toISOString(),
            });
          });
        });
      }
      
      // Add memberships to details
      if (uniqueMemberships.length > 0) {
        uniqueMemberships.forEach(membershipId => {
          const detailId = detailIndex++;
          details.push({
            tempDetailId: detailId,
            bookingDetail_Id: 0,
            entity_Id: membershipId,
            entity_Type: 'mem',
            is_Cancelled: false,
            cancel_Reason: null,
            created_By: currentUserId,
            created_On: new Date().toISOString(),
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
          });
          
          // Add resource details for all services in this membership
          const servicesInMembership = membershipServices.filter(r => r.membershipId === membershipId);
          servicesInMembership.forEach(service => {
            // Create local date-time without timezone conversion
            const bookingDate = new Date(form.booking_Date);
            const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), 
                                     service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
            const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), 
                                   service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

            resourceDetails.push({
              tempDetailId: detailId,
              bookingResourceDetail_Id: 0,
              bookingDetail_Id: 0,
              service_Id: service.serviceId,
              package_Id: service.packageId,
              membership_Id: membershipId,
              room_Id: service.roomId,
              equipment_Id: service.equipmentId,
              employee_Id: service.employeeId,
              from_Time: formatLocalDateTime(fromTime),
              to_Time: formatLocalDateTime(toTime),
              modified_By: currentUserId,
              modified_On: new Date().toISOString(),
            });
          });
        });
      }
      
      // Determine the source value
      let sourceValue = null;
      if (form.source === 'others') {
        sourceValue = form.sourceOther.trim();
      } else if (form.source) {
        sourceValue = form.source;
      }

      // Calculate the earliest from time and latest to time from all services
      const bookingDate = new Date(form.booking_Date);
      const allFromTimes = resourceDetails.map(rd => rd.from_Time ? new Date(rd.from_Time) : null).filter(d => d !== null);
      const allToTimes = resourceDetails.map(rd => rd.to_Time ? new Date(rd.to_Time) : null).filter(d => d !== null);
      
      // Use booking date as default if no times are available
      const earliestFromTime = allFromTimes.length > 0 ? new Date(Math.min(...allFromTimes)) : bookingDate;
      const latestToTime = allToTimes.length > 0 ? new Date(Math.max(...allToTimes)) : bookingDate;

      const bookingData = {
        ...form,
        booking_Date: formatLocalDate(new Date(form.booking_Date)),
        from_Time: formatLocalDateTime(earliestFromTime),
        to_Time: formatLocalDateTime(latestToTime),
        entry_Date: formatLocalDateTime(new Date()),
        source: sourceValue,
        modified_By: currentUserId,
        modified_On: formatLocalDateTime(new Date()),
        details: details,
        resourceDetails: resourceDetails,
      };

      const result = await saveBooking(bookingData);
      
      if (result.id && result.msg) {
        Swal.fire("Success", result.msg || result.message || "Booking saved successfully", "success");
        resetForm();
        if (activeTab === "2") {
          loadBookings();
        }
      } else {
        Swal.fire("Error", result.msg || result.message || "Failed to save booking", "error");
      }
    } catch (error) {
      console.error("Error saving booking:", error);
      Swal.fire("Error", "Failed to save booking", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      booking_Id: 0,
      customer_Id: null,
      employee_Id: null,
      room_Id: null,
      from_Time: new Date(),
      to_Time: new Date(),
      entry_Date: new Date(),
      booking_Date: new Date(),
      is_Cancelled: false,
      cancellation_Reason: "",
      notes: "",
      source: null,
      sourceOther: "",
      modified_By: null,
      modified_On: null,
      selectedServices: [],
      selectedPackages: [],
      selectedPackageSeries: [],
    });
    setServiceResources([]);
    setSelectedCustomerOption(null);
    setIsEditing(false);
    setIsViewing(false);
    setFormKey(prev => prev + 1);
    
    // Reset membership data
    setCustomerMembership(null);
    setHasMembership(false);
    setMembershipServices([]);
    setMembershipPackages([]);
    setMembershipPackageSeries([]);
    
    // Reset membership modal state
    setShowMembershipModal(false);
    setSelectedMembershipServices([]);
    setSelectedMembershipPackages([]);
    setSelectedMembershipPackageSeries([]);
  };

  const handleEdit = async (booking) => {
    // Load existing booking details if available
    let selectedServices = [];
    let selectedPackages = [];
    let selectedPackageSeries = [];

    if (booking.details && Array.isArray(booking.details)) {
      for (const detail of booking.details) {
        if (detail.entity_Type === 'ser') {
          const service = services.find(s => s.value === detail.entity_Id);
          if (service) {
            selectedServices.push({
              ...service,
              bookingDetail_Id: detail.bookingDetail_Id,
              is_Cancelled: detail.is_Cancelled,
              cancel_Reason: detail.cancel_Reason
            });
          }
        } else if (detail.entity_Type === 'pac') {
          const pkg = packages.find(p => p.value === detail.entity_Id);
          if (pkg) {
            selectedPackages.push({
              ...pkg,
              bookingDetail_Id: detail.bookingDetail_Id,
              is_Cancelled: detail.is_Cancelled,
              cancel_Reason: detail.cancel_Reason
            });
          }
        } else if (detail.entity_Type === 'sep') {
          const series = packageSeries.find(s => s.value === detail.entity_Id);
          if (series) {
            selectedPackageSeries.push({
              ...series,
              bookingDetail_Id: detail.bookingDetail_Id,
              is_Cancelled: detail.is_Cancelled,
              cancel_Reason: detail.cancel_Reason
            });
          }
        } else if (detail.entity_Type === 'mem') {
          // Handle membership bookings - get package series from membership
          const membershipSeries = await getPackageSeriesFromMembership(detail.entity_Id);
          if (membershipSeries.length > 0) {
            // For membership, we'll show the first available series
            // In a real scenario, you might want to store which specific series was selected
            selectedPackageSeries.push({
              ...membershipSeries[0],
              bookingDetail_Id: detail.bookingDetail_Id,
              is_Cancelled: detail.is_Cancelled,
              cancel_Reason: detail.cancel_Reason,
              isMembership: true,
              membershipId: detail.entity_Id
            });
          }
        }
      }
    }

    // Determine source and sourceOther values for edit
    let sourceValue = null;
    let sourceOtherValue = "";
    
    if (booking.source) {
      if (booking.source === 'new' || booking.source === 'repeat') {
        sourceValue = booking.source;
      } else {
        // If it's not 'new' or 'repeat', it's a custom value
        sourceValue = 'others';
        sourceOtherValue = booking.source;
      }
    }

    // Get employee and room IDs from resource details (first available)
    const firstResource = booking.details?.[0]?.resourceDetails?.[0];
    const employeeId = firstResource?.employee_Id || null;
    const roomId = firstResource?.room_Id || null;

    setForm({
      booking_Id: booking.booking_Id,
      customer_Id: booking.customer_Id,
      employee_Id: employeeId,
      room_Id: roomId,
      from_Time: booking.from_Time ? new Date(booking.from_Time) : null,
      to_Time: booking.to_Time ? new Date(booking.to_Time) : null,
      entry_Date: new Date(booking.entry_Date),
      booking_Date: new Date(booking.booking_Date),
      is_Cancelled: booking.is_Cancelled,
      cancellation_Reason: booking.cancellation_Reason || "",
      notes: booking.notes || "",
      source: sourceValue, // 👈 SET SOURCE FOR EDIT
      sourceOther: sourceOtherValue, // 👈 SET SOURCE OTHER FOR EDIT
      modified_By: booking.modified_By,
      modified_On: booking.modified_On,
      selectedServices: selectedServices,
      selectedPackages: selectedPackages,
      selectedPackageSeries: selectedPackageSeries,
    });

    // Populate service resources for editing
    const serviceResources = [];
    if (booking.details && Array.isArray(booking.details)) {
      for (const detail of booking.details) {
        if (detail.resourceDetails && Array.isArray(detail.resourceDetails)) {
          for (const resource of detail.resourceDetails) {
            const service = services.find(s => s.value === resource.service_Id);
            const serviceName = service ? service.label : `Service ${resource.service_Id}`;
            const packageName = resource.package_Id ? packages.find(p => p.value === resource.package_Id)?.label : null;
            
            serviceResources.push({
              id: `edit-${resource.bookingResourceDetail_Id}`,
              serviceId: resource.service_Id,
              serviceName: serviceName,
              packageId: resource.package_Id,
              packageName: packageName,
              membershipId: resource.membership_Id,
              employeeId: resource.employee_Id,
              roomId: resource.room_Id,
              equipmentId: resource.equipment_Id,
              fromTime: resource.from_Time ? new Date(resource.from_Time) : null,
              toTime: resource.to_Time ? new Date(resource.to_Time) : null
            });
          }
        }
      }
    }
    setServiceResources(serviceResources);
    
    // Check if customer has membership for the badge
    await checkCustomerMembership(booking.customer_Id);
    
    setIsEditing(true);
    setIsViewing(false);
    setActiveTab("1");
    
    // Trigger availability check after form is loaded
    setTimeout(() => {
      checkAvailabilityForSelection();
    }, 100);
  };

  // New function to handle cancellation with reason
  const handleCancel = async (bookingId) => {
    setCancellingBookingId(bookingId);
    setCancelReason("");
    setShowCancelModal(true);
  };

  // Function to handle Pay Bill navigation
  const handlePayBill = (booking) => {
    console.log('Pay Bill clicked for booking:', booking);
    console.log('Customer ID:', booking.customer_Id);
    console.log('Booking ID:', booking.booking_Id);
    console.log('Booking details:', booking.details);
    
    // Navigate to billing page with customer ID and booking details
    navigate('/GmsBilling', { 
      state: { 
        customerId: booking.customer_Id,
        bookingId: booking.booking_Id,
        bookingDetails: booking.details
      } 
    });
  };

  // Function to handle View Bill navigation
  const handleViewBill = async (booking) => {
    try {
      console.log('View Bill clicked for booking:', booking);
      console.log('Invoice ID:', booking.invoice_Id);
      
      // Fetch the invoice details using the booking's invoice_Id
      const invoiceData = await getSalesInvoiceById(booking.invoice_Id);
      
      if (invoiceData) {
        // Navigate to BillingPage with invoice data for read-only view
        navigate('/GmsBilling', { 
          state: { 
            viewInvoice: invoiceData,
            isReadOnly: true 
          } 
        });
      } else {
        Swal.fire("Error", "Invoice not found", "error");
      }
    } catch (error) {
      console.error("Error loading invoice:", error);
      Swal.fire("Error", "Failed to load invoice details", "error");
    }
  };

  // Function to handle bulk completion status update
  const handleBulkCompletionStatusUpdate = async (booking) => {
    try {
      // First check if all services have employees assigned
      if (!checkIfAllServicesHaveEmployees(booking)) {
        Swal.fire({
          title: 'Cannot Complete',
          text: 'All services must have employees assigned before marking as done.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      // Check if any services are pending
      const hasPendingServices = checkIfAnyServicePending(booking);
      const newStatus = hasPendingServices ? 'Complete' : 'Pending';
      
      // Show confirmation dialog
      const actionText = hasPendingServices ? 'mark all services as complete' : 'mark all services as pending';
      const result = await Swal.fire({
        title: 'Confirm Action',
        text: `Are you sure you want to ${actionText}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, do it!'
      });

      if (result.isConfirmed) {
        // Call the bulk update API
        const response = await updateBulkCompletionStatus(booking.booking_Id, newStatus);
        
        if (response.isSuccess) {
          Swal.fire("Success", response.message, "success");
          // Refresh the booking list
          loadBookings();
        } else {
          Swal.fire("Error", response.message, "error");
        }
      }
    } catch (error) {
      console.error("Error updating bulk completion status:", error);
      Swal.fire("Error", "Failed to update services", "error");
    }
  };

  // Helper function to check if any services are pending
  const checkIfAnyServicePending = (booking) => {
    if (!booking.details || booking.details.length === 0) {
      return false;
    }
    
    for (const detail of booking.details) {
      if (!detail.resourceDetails || detail.resourceDetails.length === 0) {
        continue;
      }
      
      for (const service of detail.resourceDetails) {
        if (service.completionStatus !== 'Complete') {
          return true;
        }
      }
    }
    
    return false;
  };

  // Helper function to check if all services have employees assigned
  const checkIfAllServicesHaveEmployees = (booking) => {
    if (!booking.details || booking.details.length === 0) {
      return false;
    }
    
    for (const detail of booking.details) {
      if (!detail.resourceDetails || detail.resourceDetails.length === 0) {
        continue;
      }
      
      for (const service of detail.resourceDetails) {
        // Check if employee_Id is null or undefined
        if (!service.employee_Id) {
          return false;
        }
      }
    }
    
    return true;
  };

  // Function to handle viewing a booking
  const handleView = async (booking) => {
    // Ensure all required data is loaded before processing
    if (services.length === 0 || packages.length === 0 || employees.length === 0 || rooms.length === 0) {
      try {
        await Promise.all([
          services.length === 0 ? loadServices() : Promise.resolve(),
          packages.length === 0 ? loadPackages() : Promise.resolve(),
          employees.length === 0 ? loadEmployees() : Promise.resolve(),
          rooms.length === 0 ? loadRooms() : Promise.resolve(),
        ]);
      } catch (error) {
        console.error('Error loading required data for view:', error);
        Swal.fire("Error", "Failed to load required data", "error");
        return;
      }
    }

    // Load existing booking details if available
    let selectedServices = [];
    let selectedPackages = [];
    let selectedPackageSeries = [];

    if (booking.details && Array.isArray(booking.details)) {
      for (const detail of booking.details) {
        if (detail.entity_Type === 'ser') {
          const service = services.find(s => s.value === detail.entity_Id);
          if (service) {
            selectedServices.push({
              ...service,
              bookingDetail_Id: detail.bookingDetail_Id,
              is_Cancelled: detail.is_Cancelled,
              cancel_Reason: detail.cancel_Reason
            });
          }
        } else if (detail.entity_Type === 'pac') {
          const pkg = packages.find(p => p.value === detail.entity_Id);
          if (pkg) {
            selectedPackages.push(pkg);
          }
        } else if (detail.entity_Type === 'sep') {
          const series = packageSeries.find(s => s.value === detail.entity_Id);
          if (series) {
            selectedPackageSeries.push(series);
          }
        } else if (detail.entity_Type === 'mem') {
          // Handle membership bookings - get package series from membership
          const membershipSeries = await getPackageSeriesFromMembership(detail.entity_Id);
          if (membershipSeries.length > 0) {
            // For membership, we'll show the first available series
            selectedPackageSeries.push({
              ...membershipSeries[0],
              isMembership: true,
              membershipId: detail.entity_Id
            });
          }
        }
      }
    }

    // Collect all services from resourceDetails (including those from packages and memberships)
    const allServiceIds = new Set();
    if (booking.details && Array.isArray(booking.details)) {
      for (const detail of booking.details) {
        if (detail.resourceDetails && Array.isArray(detail.resourceDetails)) {
          for (const resource of detail.resourceDetails) {
            if (resource.service_Id) {
              allServiceIds.add(resource.service_Id);
            }
          }
        }
      }
    }

    // Add all unique services to selectedServices
    for (const serviceId of allServiceIds) {
      const service = services.find(s => s.value === serviceId);
      if (service && !selectedServices.find(s => s.value === serviceId)) {
        selectedServices.push(service);
      }
    }

    // Determine source and sourceOther values for view
    let sourceValue = null;
    let sourceOtherValue = "";
    
    if (booking.source) {
      if (booking.source === 'new' || booking.source === 'repeat') {
        sourceValue = booking.source;
      } else {
        // If it's not 'new' or 'repeat', it's a custom value
        sourceValue = 'others';
        sourceOtherValue = booking.source;
      }
    }

    // Get employee and room IDs from resource details (first available)
    const firstResource = booking.details?.[0]?.resourceDetails?.[0];
    const employeeId = firstResource?.employee_Id || null;
    const roomId = firstResource?.room_Id || null;

    setForm({
      booking_Id: booking.booking_Id,
      customer_Id: booking.customer_Id,
      employee_Id: employeeId,
      room_Id: roomId,
      from_Time: booking.from_Time ? new Date(booking.from_Time) : null,
      to_Time: booking.to_Time ? new Date(booking.to_Time) : null,
      entry_Date: new Date(booking.entry_Date),
      booking_Date: new Date(booking.booking_Date),
      is_Cancelled: booking.is_Cancelled,
      cancellation_Reason: booking.cancellation_Reason || "",
      notes: booking.notes || "",
      source: sourceValue, // 👈 SET SOURCE FOR VIEW
      sourceOther: sourceOtherValue, // 👈 SET SOURCE OTHER FOR VIEW
      modified_By: booking.modified_By,
      modified_On: booking.modified_On,
      selectedServices: selectedServices,
      selectedPackages: selectedPackages,
      selectedPackageSeries: selectedPackageSeries,
    });

    // Populate service resources for viewing
    const serviceResources = [];
    if (booking.details && Array.isArray(booking.details)) {
      for (const detail of booking.details) {
        if (detail.resourceDetails && Array.isArray(detail.resourceDetails)) {
          for (const resource of detail.resourceDetails) {
            const service = services.find(s => s.value === resource.service_Id);
            const serviceName = service ? service.label : `Service ${resource.service_Id}`;
            const packageName = resource.package_Id ? packages.find(p => p.value === resource.package_Id)?.label : null;
            const employee = employees.find(emp => emp.value === resource.employee_Id);
            const employeeName = employee ? employee.label : (resource.employee_Id ? `Employee ${resource.employee_Id}` : '-');
            const room = rooms.find(room => room.value === resource.room_Id);
            const roomName = room ? room.label : (resource.room_Id ? `Room ${resource.room_Id}` : '-');
            
            console.log('Processing resource:', {
              serviceId: resource.service_Id,
              serviceName: serviceName,
              packageId: resource.package_Id,
              packageName: packageName,
              employeeId: resource.employee_Id,
              employeeName: employeeName,
              roomId: resource.room_Id,
              roomName: roomName
            });
            
            console.log('Available data for mapping:', {
              services: services.length,
              packages: packages.length,
              employees: employees.length,
              rooms: rooms.length,
              foundService: service,
              foundPackage: resource.package_Id ? packages.find(p => p.value === resource.package_Id) : null,
              foundEmployee: employee,
              foundRoom: room
            });
            
            serviceResources.push({
              id: `view-${resource.bookingResourceDetail_Id}`,
              serviceId: resource.service_Id,
              serviceName: serviceName,
              packageId: resource.package_Id,
              packageName: packageName,
              membershipId: resource.membership_Id,
              employeeId: resource.employee_Id,
              employeeName: employeeName,
              roomId: resource.room_Id,
              roomName: roomName,
              equipmentId: resource.equipment_Id,
              fromTime: new Date(resource.from_Time),
              toTime: new Date(resource.to_Time)
            });
          }
        }
      }
    }
    console.log('Total serviceResources:', serviceResources.length, serviceResources);
    setServiceResources(serviceResources);
    
    // Check if customer has membership for the badge
    await checkCustomerMembership(booking.customer_Id);
    
    setIsViewing(true);
    setIsEditing(false);
    setActiveTab("1");
  };

  // Function to handle individual entity cancellation during edit
  const handleCancelEntity = (entityType, index) => {
    let entityName = "";
    
    switch (entityType) {
      case 'service':
        entityName = form.selectedServices[index]?.label || 'Service';
        break;
      case 'package':
        entityName = form.selectedPackages[index]?.label || 'Package';
        break;
      case 'series':
        entityName = form.selectedPackageSeries[index]?.label || 'Package Series';
        break;
      default:
        entityName = 'Item';
    }

    // Set the entity to be cancelled and show the modal
    setCancellingEntity({ type: entityType, index: index, name: entityName });
    setEntityCancelReason("");
    setShowEntityCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!cancelReason.trim()) {
      Swal.fire("Validation Error", "Please provide a cancellation reason", "error");
      return;
    }

    try {
      setLoading(true);
      const booking = bookings.find(b => b.booking_Id === cancellingBookingId);
      if (!booking) {
        Swal.fire("Error", "Booking not found", "error");
        return;
      }
      
      const cancelData = {
        ...booking,
        is_Cancelled: true,
        cancellation_Reason: cancelReason.trim(),
      };
      
      const result = await saveBooking(cancelData);
      
      if (result.id && result.msg) {
        Swal.fire("Cancelled!", "Booking has been cancelled.", "success");
        setShowCancelModal(false);
        setCancellingBookingId(null);
        setCancelReason("");
        loadBookings();
      } else {
        Swal.fire("Error", result.message || result.msg || "Failed to cancel booking", "error");
      }
    } catch (error) {
      console.error("Error cancelling booking:", error);
      Swal.fire("Error", "Failed to cancel booking", "error");
    } finally {
      setLoading(false);
    }
  };

  const confirmEntityCancel = async () => {
    if (!entityCancelReason.trim()) {
      Swal.fire("Validation Error", "Please provide a cancellation reason", "error");
      return;
    }

    try {
      const { type, index, name } = cancellingEntity;
      
      // Update the entity with cancellation reason and mark as cancelled
      switch (type) {
        case 'service':
          const newServices = [...form.selectedServices];
          newServices[index] = { ...newServices[index], cancel_Reason: entityCancelReason.trim(), is_Cancelled: true };
          handleFormChange('selectedServices', newServices);
          break;
        case 'package':
          const newPackages = [...form.selectedPackages];
          newPackages[index] = { ...newPackages[index], cancel_Reason: entityCancelReason.trim(), is_Cancelled: true };
          handleFormChange('selectedPackages', newPackages);
          break;
        case 'series':
          const newSeries = [...form.selectedPackageSeries];
          newSeries[index] = { ...newSeries[index], cancel_Reason: entityCancelReason.trim(), is_Cancelled: true };
          handleFormChange('selectedPackageSeries', newSeries);
          break;
      }
      
      Swal.fire("Cancelled!", `${name} has been cancelled with reason: ${entityCancelReason.trim()}`, "success");
      setShowEntityCancelModal(false);
      setCancellingEntity({ type: '', index: -1, name: '' });
      setEntityCancelReason("");
    } catch (error) {
      console.error("Error cancelling entity:", error);
      Swal.fire("Error", "Failed to cancel entity", "error");
    }
  };

  // Membership modal functions
  const openMembershipModal = () => {
    if (hasMembership && customerMembership) {
      setShowMembershipModal(true);
      // Reset selections when opening modal
      setSelectedMembershipServices([]);
      setSelectedMembershipPackages([]);
      setSelectedMembershipPackageSeries([]);
      // Reset hierarchical selection to reflect current state
      setHierarchicalSelection({
        packageSeries: {},
        packages: {},
        services: {}
      });
    }
  };

  const closeMembershipModal = () => {
    setShowMembershipModal(false);
    setSelectedMembershipServices([]);
    setSelectedMembershipPackages([]);
    setSelectedMembershipPackageSeries([]);
    setHierarchicalSelection({
      packageSeries: {},
      packages: {},
      services: {}
    });
  };

  // Build hierarchical data structure for membership items
  const buildHierarchicalData = (membership) => {
    if (!membership || !membership.details) return { packageSeries: [], packages: [], services: [] };

    const hierarchicalData = {
      packageSeries: [],
      packages: [],
      services: []
    };

    // Get already added membership services from serviceResources
    const addedMembershipServices = serviceResources
      .filter(r => r.membershipId === membership.membershipId)
      .map(r => r.serviceId);

    // Get unique package series from installments
    const uniqueSeriesIds = [...new Set(membership.installments.map(installment => installment.seriesId))];
    
    uniqueSeriesIds.forEach(seriesId => {
      const series = packageSeries.find(s => s.value === seriesId);
      if (series) {
        // Get packages that belong to this series from membership details
        // Since details don't have seriesId, we'll get all packages from details and match them with series
        const uniquePackageIds = [...new Set(membership.details.map(detail => detail.packageId))];

        const packagesInSeries = uniquePackageIds.map(packageId => {
          const pkg = packages.find(p => p.value === packageId);
          if (pkg) {
            // Get services that belong to this package
            const packageServices = membership.details
              .filter(detail => detail.packageId === packageId)
              .map(detail => detail.serviceId)
              .filter((serviceId, index, self) => self.indexOf(serviceId) === index);

            const servicesInPackage = packageServices
              .filter(serviceId => !addedMembershipServices.includes(serviceId)) // Filter out already added services
              .map(serviceId => {
                const service = services.find(s => s.value === serviceId);
                return service ? { ...service, packageId, seriesId } : null;
              }).filter(Boolean);

            // Only include package if it has services that haven't been added yet
            if (servicesInPackage.length > 0) {
              return {
                ...pkg,
                seriesId,
                services: servicesInPackage
              };
            }
          }
          return null;
        }).filter(Boolean);

        // Only include series if it has packages with available services
        if (packagesInSeries.length > 0) {
          hierarchicalData.packageSeries.push({
            ...series,
            packages: packagesInSeries
          });
        }
      }
    });

    return hierarchicalData;
  };

  // Handle hierarchical checkbox selection
  const handleHierarchicalSelection = (type, id, checked, parentId = null) => {
    setHierarchicalSelection(prev => {
      const newSelection = { ...prev };
      
      if (type === 'packageSeries') {
        newSelection.packageSeries[id] = checked;
        
        // If package series is selected/deselected, update all its packages and services
        const seriesData = hierarchicalData.packageSeries.find(s => s.value === id);
        if (seriesData) {
          seriesData.packages.forEach(pkg => {
            newSelection.packages[pkg.value] = checked;
            pkg.services.forEach(service => {
              newSelection.services[service.value] = checked;
            });
          });
        }
      } else if (type === 'package') {
        newSelection.packages[id] = checked;
        
        // If package is selected/deselected, update all its services
        const packageData = hierarchicalData.packageSeries
          .flatMap(s => s.packages)
          .find(p => p.value === id);
        if (packageData) {
          packageData.services.forEach(service => {
            newSelection.services[service.value] = checked;
          });
        }
        
        // Check if all packages in the series are selected
        const seriesData = hierarchicalData.packageSeries.find(s => s.value === parentId);
        if (seriesData) {
          const allPackagesSelected = seriesData.packages.every(pkg => 
            pkg.value === id ? checked : newSelection.packages[pkg.value]
          );
          newSelection.packageSeries[parentId] = allPackagesSelected;
        }
      } else if (type === 'service') {
        newSelection.services[id] = checked;
        
        // Check if all services in the package are selected
        const packageData = hierarchicalData.packageSeries
          .flatMap(s => s.packages)
          .find(p => p.value === parentId);
        if (packageData) {
          const allServicesSelected = packageData.services.every(service => 
            service.value === id ? checked : newSelection.services[service.value]
          );
          newSelection.packages[parentId] = allServicesSelected;
          
          // Check if all packages in the series are selected
          const seriesData = hierarchicalData.packageSeries.find(s => 
            s.packages.some(p => p.value === parentId)
          );
          if (seriesData) {
            const allPackagesSelected = seriesData.packages.every(pkg => 
              newSelection.packages[pkg.value]
            );
            newSelection.packageSeries[seriesData.value] = allPackagesSelected;
          }
        }
      }
      
      return newSelection;
    });
  };

  // Build hierarchical data when modal opens
  const hierarchicalData = customerMembership ? buildHierarchicalData(customerMembership) : { packageSeries: [], packages: [], services: [] };

  const applyMembershipSelections = async () => {
    // Convert hierarchical selection to flat arrays
    const selectedServices = [];
    const selectedPackages = [];
    const selectedPackageSeries = [];

    // Get selected services
    Object.keys(hierarchicalSelection.services).forEach(serviceId => {
      if (hierarchicalSelection.services[serviceId]) {
        const service = services.find(s => s.value === parseInt(serviceId));
        if (service) {
          selectedServices.push(service);
        }
      }
    });

    // Get selected packages
    Object.keys(hierarchicalSelection.packages).forEach(packageId => {
      if (hierarchicalSelection.packages[packageId]) {
        const pkg = packages.find(p => p.value === parseInt(packageId));
        if (pkg) {
          selectedPackages.push(pkg);
        }
      }
    });

    // Get selected package series
    Object.keys(hierarchicalSelection.packageSeries).forEach(seriesId => {
      if (hierarchicalSelection.packageSeries[seriesId]) {
        const series = packageSeries.find(s => s.value === parseInt(seriesId));
        if (series) {
          selectedPackageSeries.push({
            ...series,
            isMembership: true,
            membershipId: customerMembership.membershipId
          });
        }
      }
    });

    // Update service resources for membership selections
    const newServiceResources = [...serviceResources];

    // Add package services from membership first
    if (selectedPackages.length > 0) {
      const packageIds = selectedPackages.map(pkg => pkg.value);
      const packageServices = await fetchPackageServices(packageIds);
      
      // Calculate time slots for membership package services
      const baseTime = new Date();
      baseTime.setHours(9, 0, 0, 0); // Start at 9:00 AM
      let cumulativeMinutes = 0;
      
      packageServices.forEach((service, index) => {
        const resourceId = `mem-pkg-${service.packageId}-${service.serviceId}`;
        if (!newServiceResources.find(r => r.id === resourceId)) {
          // Calculate time slots using service duration
          const fromTime = new Date(baseTime.getTime() + (cumulativeMinutes * 60000));
          const duration = service.duration || 30; // Use service duration from API
          const toTime = new Date(fromTime.getTime() + (duration * 60000));
          
          newServiceResources.push({
            id: resourceId,
            serviceId: service.serviceId,
            serviceName: service.label,
            packageId: service.packageId,
            packageName: service.packageName,
            membershipId: customerMembership.membershipId,
            employeeId: null,
            roomId: null,
            equipmentId: null,
            fromTime: fromTime,
            toTime: toTime
          });
          
          // Update cumulative minutes for next service
          cumulativeMinutes += duration;
        }
      });
    }

    // Add individual membership services (only if not already added as part of a package)
    const packageServiceIds = newServiceResources
      .filter(r => r.packageId && r.membershipId)
      .map(r => r.serviceId);
    
    // Calculate time slots for individual membership services
    const baseTimeForIndividual = new Date();
    baseTimeForIndividual.setHours(9, 0, 0, 0); // Start at 9:00 AM
    let cumulativeMinutesForIndividual = 0;
    
    selectedServices.forEach(service => {
      // Only add if this service is not already part of a selected package
      if (!packageServiceIds.includes(service.value)) {
        const resourceId = `mem-srv-${service.value}`;
        if (!newServiceResources.find(r => r.id === resourceId)) {
          // Check if this service belongs to a package in the membership
          const servicePackageInfo = customerMembership.details.find(detail => detail.serviceId === service.value);
          const packageId = servicePackageInfo ? servicePackageInfo.packageId : null;
          const packageName = packageId ? packages.find(p => p.value === packageId)?.label : null;
          
          // Calculate time slots using service duration
          const fromTime = new Date(baseTimeForIndividual.getTime() + (cumulativeMinutesForIndividual * 60000));
          const duration = service.duration || 30; // Use service duration from API
          const toTime = new Date(fromTime.getTime() + (duration * 60000));
          
          newServiceResources.push({
            id: resourceId,
            serviceId: service.value,
            serviceName: service.label,
            packageId: packageId,
            packageName: packageName,
            membershipId: customerMembership.membershipId,
            employeeId: null,
            roomId: null,
            equipmentId: null,
            fromTime: fromTime,
            toTime: toTime
          });
          
          // Update cumulative minutes for next service
          cumulativeMinutesForIndividual += duration;
        }
      }
    });

    setServiceResources(newServiceResources);
    closeMembershipModal();
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove empty strings and convert to proper format
      const cleanFilter = {
        ...(filter.customer_Id !== null && filter.customer_Id !== undefined && { Customer_Id: filter.customer_Id }),
        ...(filter.employee_Id !== null && filter.employee_Id !== undefined && { Employee_Id: filter.employee_Id }),
        ...(filter.room_Id !== null && filter.room_Id !== undefined && { Room_Id: filter.room_Id }),
        ...(filter.from_Date && { From_Date: filter.from_Date }),
        ...(filter.to_Date && { To_Date: filter.to_Date })
      };
      
      await exportBookingsToExcel(cleanFilter);
      Swal.fire("Success", "Booking report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting bookings:", error);
      Swal.fire("Error", "Failed to export booking report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExportDetails = async (bookingId) => {
    try {
      setLoading(true);
      await exportBookingDetailsToExcel(bookingId);
      Swal.fire("Success", "Booking details exported successfully", "success");
    } catch (error) {
      console.error("Error exporting booking details:", error);
      Swal.fire("Error", "Failed to export booking details", "error");
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = () => {
    setPage(1);
    loadBookings();
  };

  const clearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    setFilterKey(prev => prev + 1);
    setCustomerSearchOptions([]);
    setSelectedCustomerFilterOption(null);
    loadBookings(); // Use the main loadBookings function for consistency
  };


  const formatDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  const formatTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return "N/A";
    }
  };

  const formatDate = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.value === customerId);
    return customer ? customer.label : "N/A";
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.value === employeeId);
    return employee ? employee.label : "N/A";
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.value === roomId);
    return room ? room.label : "N/A";
  };

  // Get availability status for employee
  const getEmployeeAvailability = (employeeId) => {
    const key = `Employee_${employeeId}`;
    return availabilityResults[key] || { isAvailable: true, conflicts: [] };
  };

  // Get availability status for room
  const getRoomAvailability = (roomId) => {
    const key = `Room_${roomId}`;
    return availabilityResults[key] || { isAvailable: true, conflicts: [] };
  };

  // Create employee option with availability indicator
  const createEmployeeOption = (employee) => {
    const availability = getEmployeeAvailability(employee.value);
    const isSelected = form.employee_Id === employee.value;
    
    return {
      ...employee,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{employee.label}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {availability.isAvailable ? (
              <span style={{ color: '#28a745', fontSize: '12px' }}>
                ✓ Available
              </span>
            ) : (
              <span style={{ color: '#dc3545', fontSize: '12px' }}>
                ✗ Busy
              </span>
            )}
            {checkingAvailability && (
              <div style={{ width: '12px', height: '12px', border: '2px solid #f3f3f3', borderTop: '2px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            )}
          </div>
        </div>
      ),
      isDisabled: !availability.isAvailable && !isSelected
    };
  };

  // Create room option with availability indicator
  const createRoomOption = (room) => {
    const availability = getRoomAvailability(room.value);
    const isSelected = form.room_Id === room.value;
    
    return {
      ...room,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{room.label}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {availability.isAvailable ? (
              <span style={{ color: '#28a745', fontSize: '12px' }}>
                ✓ Available
              </span>
            ) : (
              <span style={{ color: '#dc3545', fontSize: '12px' }}>
                ✗ Busy
              </span>
            )}
            {checkingAvailability && (
              <div style={{ width: '12px', height: '12px', border: '2px solid #f3f3f3', borderTop: '2px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            )}
          </div>
        </div>
      ),
      isDisabled: !availability.isAvailable && !isSelected
    };
  };

  const getStatusBadge = (booking) => {
    if (booking.is_Cancelled) {
      return <span className="badge bg-danger">Cancelled</span>;
    }
    
    if (areAllServicesComplete(booking)) {
      return <span className="badge bg-info">Completed</span>;
    }
    
    return <span className="badge bg-success">Active</span>;
  };

  // Pagination component
  const Pagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    const pages = [];
    
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
        </div>
        <div className="d-flex gap-1">
          <Button
            color="primary"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          {pages.map(pageNum => (
            <Button
              key={pageNum}
              color={pageNum === page ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => setPage(pageNum)}
            >
              {pageNum}
            </Button>
          ))}
          <Button
            color="primary"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <style>{spinnerStyle}</style>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg="12">
              <Breadcrumb title="Booking Management" breadcrumbItem="Spa" />
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">
                    <Calendar className="me-2" size={20} />
                    Booking Management
                  </h4>

                  <Nav tabs className="nav-tabs-custom">
                    {hasEditOrHigher && (
                      <NavItem>
                        <NavLink
                          style={{ 
                            cursor: "pointer",
                            fontWeight: activeTab === "1" ? "bold" : "normal",
                            color: activeTab === "1" ? "#556ee6" : "#74788d",
                            borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                            padding: "15px 25px",
                            fontSize: "16px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                          className={activeTab === "1" ? "active" : ""}
                          onClick={() => setActiveTab("1")}
                        >
                          <i className="bx bx-plus-circle"></i>
                          <span>{isViewing ? "View Booking" : isEditing ? "Edit Booking" : "Add Booking"}</span>
                        </NavLink>
                      </NavItem>
                    )}
                    <NavItem>
                      <NavLink
                        style={{ 
                          cursor: "pointer",
                          fontWeight: activeTab === "2" ? "bold" : "normal",
                          color: activeTab === "2" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "2" ? "active" : ""}
                        onClick={() => setActiveTab("2")}
                      >
                        <i className="bx bx-list-ul"></i>
                        <span>View Bookings</span>
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={activeTab} className="mt-3">
                    <TabPane tabId="1">
                      <Form onSubmit={handleSubmit}>
                        <Row>
                          <Col md="6">
                            <FormGroup className="mb-3">
                              <Label for="customer" className="mb-2">
                                Customer *
                                {hasMembership && (
                                  <span className="badge bg-success ms-2">
                                    <i className="mdi mdi-crown me-1"></i>
                                    Membership
                                  </span>
                                )}
                              </Label>
                              <div className="d-flex gap-2">
                                <div style={{ flex: 1 }}>
                                  <Select
                                    key={`customer-${formKey}`}
                                    value={selectedCustomerOption}
                                    onChange={(option) => {
                                      setSelectedCustomerOption(option);
                                      handleFormChange('customer_Id', option?.value);
                                    }}
                                    options={customerSearchOptions}
                                    placeholder="Type at least 3 characters to search customers..."
                                    styles={selectStyles}
                                    isClearable
                                    isSearchable
                                    isDisabled={isEditing || isViewing}
                                    isLoading={customerSearchLoading}
                                    noOptionsMessage={() => "Type at least 3 characters to search"}
                                    loadingMessage={() => "Searching customers..."}
                                    onInputChange={(inputValue) => {
                                      console.log('🔤 Input changed:', inputValue);
                                      
                                      // Clear previous timeout
                                      if (searchTimeout) {
                                        clearTimeout(searchTimeout);
                                      }
                                      
                                      // Set new timeout
                                      const timeout = setTimeout(() => {
                                        searchCustomersManually(inputValue);
                                      }, 300);
                                      setSearchTimeout(timeout);
                                      
                                      return inputValue;
                                    }}
                                    onMenuOpen={() => console.log('📂 Menu opened')}
                                    onMenuClose={() => console.log('📁 Menu closed')}
                                  />
                                </div>
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={() => setShowQuickAddCustomer(true)}
                                  disabled={isEditing || isViewing}
                                  title="Quick Add Customer"
                                >
                                  <i className="fas fa-plus"></i>
                                </Button>
                                {hasMembership && (
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={openMembershipModal}
                                    disabled={isEditing || isViewing}
                                    title="Select Membership Items"
                                  >
                                    <i className="mdi mdi-crown"></i>
                                  </Button>
                                )}
                              </div>
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup className="mb-3">
                              <Label for="bookingDate" className="mb-2">Booking Date *</Label>
                              <DatePicker
                                selected={form.booking_Date}
                                onChange={(date) => handleFormChange('booking_Date', date)}
                                className="form-control"
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Select Date"
                                disabled={isViewing}
                              />
                            </FormGroup>
                          </Col>
                        </Row>


                        {/* New row for packages and services */}
                        <Row>
                          <Col md="6">
                            <FormGroup className="mb-3">
                              <Label for="packages" className="mb-2">Packages</Label>
                              <Select
                                key={`packages-${formKey}`}
                                value={form.selectedPackages}
                                onChange={(options) => handleFormChange('selectedPackages', options || [])}
                                options={packages}
                                placeholder="Select Packages"
                                styles={selectStyles}
                                isMulti
                                isClearable={!isEditing}
                                isSearchable
                                isDisabled={isViewing}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup className="mb-3">
                              <Label for="services" className="mb-2">Services</Label>
                              <Select
                                key={`services-${formKey}`}
                                value={form.selectedServices}
                                onChange={(options) => handleFormChange('selectedServices', options || [])}
                                options={services}
                                placeholder="Select Services"
                                styles={selectStyles}
                                isMulti
                                isClearable={!isEditing}
                                isSearchable
                                isDisabled={isViewing}
                              />
                            </FormGroup>
                          </Col>
                          {/* Package Series dropdown - commented out for now
                          <Col md="4">
                            <FormGroup className="mb-3">
                              <Label for="packageSeries" className="mb-2">Package Series</Label>
                              <Select
                                key={`packageSeries-${formKey}`}
                                value={form.selectedPackageSeries}
                                onChange={(options) => handleFormChange('selectedPackageSeries', options || [])}
                                options={packageSeries}
                                placeholder="Select Package Series"
                                styles={selectStyles}
                                isMulti
                                isClearable={!isEditing}
                                isSearchable
                                isDisabled={isViewing}
                              />
                            </FormGroup>
                          </Col>
                          */}
                        </Row>

                        {/* Service Resource Allocation */}
                        {serviceResources.length > 0 && (
                          <Row>
                            <Col md="12">
                              <FormGroup className="mb-3">
                                <Label className="mb-2">Service Resource Allocation</Label>
                                <div className="border rounded p-3">
                                  <Table responsive striped>
                                    <thead>
                                      <tr>
                                        <th style={{ width: '25%' }}>Service</th>
                                        <th style={{ width: '12%' }}>From Time</th>
                                        <th style={{ width: '12%' }}>To Time</th>
                                        <th style={{ width: '18%' }}>Employee</th>
                                        <th style={{ width: '15%' }}>Room</th>
                                        <th style={{ width: '18%' }}>Equipment</th>
                                        <th style={{ width: '5%' }}>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {serviceResources.map((resource) => (
                                        <tr 
                                          key={resource.id}
                                          style={{
                                            backgroundColor: resource.packageId && !resource.membershipId ? '#f8f9fa' : 'transparent',
                                            borderLeft: resource.packageId && !resource.membershipId ? '3px solid #007bff' : 'none'
                                          }}
                                        >
                                          <td>
                                            <div>
                                              <strong>{resource.serviceName}</strong>
                                              {resource.packageName && (
                                                <div className="text-muted small">
                                                  <i className="mdi mdi-package me-1"></i>
                                                  Package: {resource.packageName}
                                    </div>
                                  )}
                                              {resource.membershipId && (
                                                <div className="text-info small">
                                                  <i className="mdi mdi-crown me-1"></i>
                                                  Membership
                                    </div>
                                          )}
                                        </div>
                                          </td>
                                          <td>
                                            {isViewing ? (
                                              <span>{resource.fromTime ? resource.fromTime.toLocaleTimeString('en-US', { 
                                                hour: 'numeric', 
                                                minute: '2-digit', 
                                                hour12: true 
                                              }) : 'N/A'}</span>
                                            ) : (
                                              <DatePicker
                                                selected={resource.fromTime}
                                                onChange={(date) => {
                                                  // Auto-calculate to time based on service duration from API
                                                  const service = services.find(s => s.value === resource.serviceId);
                                                  const duration = service?.duration || 30; // Use service duration from API (e.g., 80 minutes)
                                                  const toTime = new Date(date.getTime() + (duration * 60000));
                                                  
                                                  console.log('Service duration calculation:', { 
                                                    serviceId: resource.serviceId, 
                                                    serviceName: service?.label, 
                                                    duration: duration,
                                                    fromTime: date,
                                                    toTime: toTime
                                                  });
                                                  
                                                  // Update both from and to time
                                                  handleResourceChange(resource.id, 'fromTime', date);
                                                  handleResourceChange(resource.id, 'toTime', toTime);
                                                }}
                                                showTimeSelect
                                                showTimeSelectOnly
                                                timeIntervals={15}
                                                timeCaption="Time"
                                                dateFormat="h:mm aa"
                                                className="form-control"
                                                style={{ width: '100%' }}
                                                disabled={isViewing}
                                              />
                                            )}
                                          </td>
                                          <td>
                                            {isViewing ? (
                                              <span>{resource.toTime ? resource.toTime.toLocaleTimeString('en-US', { 
                                                hour: 'numeric', 
                                                minute: '2-digit', 
                                                hour12: true 
                                              }) : 'N/A'}</span>
                                            ) : (
                                              <DatePicker
                                                selected={resource.toTime}
                                                onChange={(date) => handleResourceChange(resource.id, 'toTime', date)}
                                                showTimeSelect
                                                showTimeSelectOnly
                                                timeIntervals={15}
                                                timeCaption="Time"
                                                dateFormat="h:mm aa"
                                                className="form-control"
                                                style={{ width: '100%' }}
                                                disabled={isViewing}
                                              />
                                            )}
                                          </td>
                                          <td>
                                            {isViewing ? (
                                              <span>{resource.employeeName || (resource.employeeId ? `Employee ${resource.employeeId}` : '-')}</span>
                                            ) : (
                                              <Select
                                                value={employees.find(emp => emp.value === resource.employeeId)}
                                                onChange={(option) => {
                                                  console.log('Employee selection attempt:', { resourceId: resource.id, option, timeAvailability: timeAvailabilityResults[resource.id] });
                                                  
                                                  // Check if employee is available before allowing selection
                                                  const timeAvailability = timeAvailabilityResults[resource.id];
                                                  if (timeAvailability && option?.value) {
                                                    const employee = timeAvailability.availableEmployees?.find(emp => emp.employee_Id === option.value);
                                                    console.log('Found employee:', employee);
                                                    if (employee && !employee.isAvailable) {
                                                      console.log('Employee not available, blocking selection');
                                                      Swal.fire("Warning", "This employee is not available for the selected time slot", "warning");
                                                      return; // Don't update if not available
                                                    }
                                                  }
                                                  // Only proceed if no availability check or employee is available
                                                  if (!timeAvailability || !option?.value) {
                                                    console.log('No availability check, allowing selection');
                                                    handleResourceChange(resource.id, 'employeeId', option?.value);
                                                  } else {
                                                    const employee = timeAvailability.availableEmployees?.find(emp => emp.employee_Id === option.value);
                                                    if (employee && employee.isAvailable) {
                                                      console.log('Employee available, allowing selection');
                                                      handleResourceChange(resource.id, 'employeeId', option?.value);
                                                    } else {
                                                      console.log('Employee not found or not available, blocking selection');
                                                    }
                                                  }
                                                }}
                                                options={formatOptionsWithAvailability(employees, 'Employee', resource.id)}
                                                placeholder="Select Employee"
                                                styles={selectStyles}
                                                isClearable
                                                isSearchable
                                                isDisabled={isViewing}
                                              />
                                            )}
                                          </td>
                                          <td>
                                            {isViewing ? (
                                              <span>{resource.roomName || (resource.roomId ? `Room ${resource.roomId}` : '-')}</span>
                                            ) : (
                                              <Select
                                                value={rooms.find(room => room.value === resource.roomId)}
                                                onChange={(option) => {
                                                  console.log('Room selection attempt:', { resourceId: resource.id, option, timeAvailability: timeAvailabilityResults[resource.id] });
                                                  
                                                  // Check if room is available before allowing selection
                                                  const timeAvailability = timeAvailabilityResults[resource.id];
                                                  if (timeAvailability && option?.value) {
                                                    const room = timeAvailability.availableRooms?.find(room => room.room_Id === option.value);
                                                    console.log('Found room:', room);
                                                    if (room && !room.isAvailable) {
                                                      console.log('Room not available, blocking selection');
                                                      Swal.fire("Warning", "This room is not available for the selected time slot", "warning");
                                                      return; // Don't update if not available
                                                    }
                                                  }
                                                  // Only proceed if no availability check or room is available
                                                  if (!timeAvailability || !option?.value) {
                                                    console.log('No availability check, allowing selection');
                                                    handleResourceChange(resource.id, 'roomId', option?.value);
                                                  } else {
                                                    const room = timeAvailability.availableRooms?.find(room => room.room_Id === option.value);
                                                    if (room && room.isAvailable) {
                                                      console.log('Room available, allowing selection');
                                                      handleResourceChange(resource.id, 'roomId', option?.value);
                                                    } else {
                                                      console.log('Room not found or not available, blocking selection');
                                                    }
                                                  }
                                                }}
                                                options={formatOptionsWithAvailability(rooms, 'Room', resource.id)}
                                                placeholder="Select Room"
                                                styles={selectStyles}
                                                isClearable
                                                isSearchable
                                                isDisabled={isViewing}
                                              />
                                            )}
                                          </td>
                                          <td>
                                            {isViewing ? (
                                              <span>
                                                {resource.equipmentId ? 
                                                  (equipments.find(eq => eq.value === resource.equipmentId)?.label || `Equipment ${resource.equipmentId}`) 
                                                  : '-'
                                                }
                                              </span>
                                            ) : (
                                              <Select
                                                value={equipments.find(eq => eq.value === resource.equipmentId)}
                                                onChange={(option) => handleResourceChange(resource.id, 'equipmentId', option?.value)}
                                                options={equipments}
                                                placeholder="Select Equipment"
                                                styles={selectStyles}
                                                isClearable
                                                isSearchable
                                                isDisabled={isViewing}
                                              />
                                            )}
                                          </td>
                                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                            {(() => {
                                              // Show remove button for individual services (both membership and non-membership)
                                              if (!resource.packageId) {
                                                return (
                                                  <Button
                                                    color="danger"
                                                    size="sm"
                                                    onClick={() => removeServiceResource(resource.id)}
                                                    disabled={isViewing}
                                                    style={{ fontSize: '12px', padding: '2px 4px', width: '25px', height: '25px' }}
                                                    title="Remove Service"
                                                  >
                                                    ×
                                                  </Button>
                                                );
                                              }
                                              
                                              // For package services
                                              if (resource.packageId) {
                                                // Membership package services - show remove button for all services
                                                if (resource.membershipId) {
                                                  return (
                                                    <Button
                                                      color="danger"
                                                      size="sm"
                                                      onClick={() => removeServiceResource(resource.id)}
                                                      disabled={isViewing}
                                                      style={{ fontSize: '12px', padding: '2px 4px', width: '25px', height: '25px' }}
                                                      title="Remove Service"
                                                    >
                                                      ×
                                                    </Button>
                                                  );
                                                }
                                                
                                                // Non-membership package services - only show remove button for the first service of the package
                                                const isFirstServiceInPackage = serviceResources.findIndex(r => 
                                                  r.packageId === resource.packageId && !r.membershipId
                                                ) === serviceResources.findIndex(r => r.id === resource.id);
                                                
                                                if (isFirstServiceInPackage) {
                                                  return (
                                                    <Button
                                                      color="danger"
                                                      size="sm"
                                                      onClick={() => removeServiceResource(resource.id)}
                                                      disabled={isViewing}
                                                      style={{ fontSize: '12px', padding: '2px 4px', width: '25px', height: '25px' }}
                                                      title="Remove Package"
                                                    >
                                                      ×
                                                    </Button>
                                                  );
                                                }
                                              }
                                              
                                              return null;
                                            })()}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              </FormGroup>
                            </Col>
                          </Row>
                        )}

                        <Row>
                          <Col md="6">
                            <FormGroup className="mb-3">
                              <Label for="source" className="mb-2">Source *</Label>
                              <Select
                                key={`source-${formKey}`}
                                value={sourceOptions.find(s => s.value === form.source)}
                                onChange={(option) => handleFormChange('source', option?.value)}
                                options={sourceOptions}
                                placeholder="Select Source"
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                isDisabled={isViewing}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            {form.source === 'others' && (
                              <FormGroup className="mb-3">
                                <Label for="sourceOther" className="mb-2">Specify Source *</Label>
                                <Input
                                  type="text"
                                  id="sourceOther"
                                  value={form.sourceOther}
                                  onChange={(e) => handleFormChange('sourceOther', e.target.value)}
                                  placeholder="Enter source details..."
                                  disabled={isViewing}
                                />
                              </FormGroup>
                            )}
                          </Col>
                        </Row>

                        <Row>
                          <Col md="12">
                            <FormGroup className="mb-4">
                              <Label for="notes" className="mb-2">Notes</Label>
                              <Input
                                type="textarea"
                                id="notes"
                                value={form.notes}
                                onChange={(e) => handleFormChange('notes', e.target.value)}
                                rows="3"
                                placeholder="Enter any additional notes..."
                                disabled={isViewing}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md="12">
                            <div className="d-flex gap-2">
                              {!isViewing && (
                                <Button
                                  type="submit"
                                  color="primary"
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <ClipLoader size={16} color="#fff" />
                                  ) : (
                                    "Save Booking"
                                  )}
                                </Button>
                              )}
                              {!isViewing && (
                                <Button
                                  type="button"
                                  color="secondary"
                                  onClick={resetForm}
                                >
                                  Clear
                                </Button>
                              )}
                              {(isEditing || isViewing) && (
                                <Button
                                  type="button"
                                  color="secondary"
                                  onClick={resetForm}
                                >
                                  {isViewing ? "Close View" : "Cancel Edit"}
                                </Button>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </TabPane>

                    <TabPane tabId="2">
                      <Row className="mb-3">
                        <Col md="12">
                          <Card>
                            <CardHeader>
                              <div className="d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0">Filter Bookings</h5>
                                {/* Only show filter button if user has edit permission */}
                                {hasEditOrHigher && (
                                  <Button
                                    style={{
                                      backgroundColor: '#556ee6',
                                      borderColor: '#556ee6',
                                      color: 'white',
                                      borderRadius: '6px',
                                      padding: '8px 16px',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                      border: 'none',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => setShowFilters(!showFilters)}
                                  >
                                    {showFilters ? "Hide Filters" : "Show Filters"}
                                  </Button>
                                )}
                              </div>
                            </CardHeader>
                            {/* Show info message for users without edit permission */}
                            {!hasEditOrHigher && (
                              <CardBody>
                                <div className="alert alert-info mb-0" role="alert">
                                  <i className="fas fa-info-circle me-2"></i>
                                  You can only view your own bookings. Contact your administrator for access to all bookings.
                                </div>
                              </CardBody>
                            )}
                            {showFilters && hasEditOrHigher && (
                            <CardBody>
                              <Row>
                                <Col md="3">
                                  <FormGroup>
                                    <Label className="mb-2">Customer</Label>
                                    <Select
                                      key={`customer-filter-${filterKey}`}
                                      value={selectedCustomerFilterOption}
                                      onChange={(option) => {
                                        setSelectedCustomerFilterOption(option);
                                        handleFilterChange('customer_Id', option?.value);
                                      }}
                                      options={customerSearchOptions}
                                      placeholder="Type at least 3 characters to search customers..."
                                      styles={selectStyles}
                                      isClearable
                                      isSearchable
                                      isLoading={customerSearchLoading}
                                      noOptionsMessage={() => "Type at least 3 characters to search"}
                                      loadingMessage={() => "Searching customers..."}
                                      onInputChange={(inputValue) => {
                                        console.log('🔤 Filter Input changed:', inputValue);
                                        
                                        // Clear previous timeout
                                        if (searchTimeout) {
                                          clearTimeout(searchTimeout);
                                        }
                                        
                                        // Set new timeout
                                        const timeout = setTimeout(() => {
                                          searchCustomersFilterManually(inputValue);
                                        }, 300);
                                        setSearchTimeout(timeout);
                                        
                                        return inputValue;
                                      }}
                                      onMenuOpen={() => console.log('📂 Filter Menu opened')}
                                      onMenuClose={() => console.log('📁 Filter Menu closed')}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md="3">
                                  <FormGroup>
                                    <Label className="mb-2">Employee</Label>
                                    <Select
                                      key={`employee-filter-${filterKey}`}
                                      value={employees.find(e => e.value === filter.employee_Id)}
                                      onChange={(option) => handleFilterChange('employee_Id', option?.value)}
                                      options={employees}
                                      placeholder="All Employees"
                                      styles={selectStyles}
                                      isClearable
                                      isSearchable
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md="3">
                                  <FormGroup>
                                    <Label className="mb-2">Room</Label>
                                    <Select
                                      key={`room-filter-${filterKey}`}
                                      value={rooms.find(r => r.value === filter.room_Id)}
                                      onChange={(option) => handleFilterChange('room_Id', option?.value)}
                                      options={rooms}
                                      placeholder="All Rooms"
                                      styles={selectStyles}
                                      isClearable
                                      isSearchable
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md="3">
                                  <FormGroup>
                                    <Label className="mb-2">From Date</Label>
                                    <DatePicker
                                      selected={filter.from_Date}
                                      onChange={(date) => handleFilterChange('from_Date', date)}
                                      className="form-control"
                                      dateFormat="dd/MM/yyyy"
                                      placeholderText="Select Date"
                                      isClearable
                                    />
                                  </FormGroup>
                                </Col>
                              </Row>
                              <Row>
                                <Col md="3">
                                  <FormGroup>
                                    <Label className="mb-2">To Date</Label>
                                    <DatePicker
                                      selected={filter.to_Date}
                                      onChange={(date) => handleFilterChange('to_Date', date)}
                                      className="form-control"
                                      dateFormat="dd/MM/yyyy"
                                      placeholderText="Select Date"
                                      isClearable
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md="9" className="d-flex align-items-end gap-2">
                                  <Button
                                    color="primary"
                                    onClick={applyFilter}
                                    disabled={loading}
                                  >
                                    {loading ? (
                                      <ClipLoader size={16} color="#fff" />
                                    ) : (
                                      "Apply Filter"
                                    )}
                                  </Button>
                                  <Button
                                    color="secondary"
                                    onClick={clearFilter}
                                    disabled={loading}
                                  >
                                    Clear Filter
                                  </Button>
                                </Col>
                              </Row>
                            </CardBody>
                            )}
                          </Card>
                        </Col>
                      </Row>

                      {/* Booking List Header with Export Button */}
                      <Row className="mb-3">
                        <Col md="12">
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Bookings List</h5>
                            <Button
                              color="success"
                              onClick={handleExportExcel}
                              disabled={loading || totalCount === 0}
                              className="waves-effect waves-light"
                            >
                              <Download className="me-1" size={16} />
                              Export Excel
                            </Button>
                          </div>
                        </Col>
                      </Row>

                      <div className="table-responsive">
                        <Table className="table-nowrap mb-0" style={{ width: '100%' }}>
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: '5%', fontSize: '14px', fontWeight: '600' }}>#</th>
                              <th style={{ width: '20%', fontSize: '14px', fontWeight: '600' }}>Customer</th>
                              {/* Removed Employee, Room, From Time, To Time columns as they vary per resource */}
                              <th style={{ width: '15%', fontSize: '14px', fontWeight: '600' }}>Booking Date</th>
                              <th style={{ width: '15%', fontSize: '14px', fontWeight: '600' }}>Entry Date</th>
                              <th style={{ width: '10%', fontSize: '14px', fontWeight: '600' }}>Status</th>
                              <th style={{ width: '35%', fontSize: '14px', fontWeight: '600' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr>
                                <td colSpan="10" className="text-center">
                                  <ClipLoader size={20} color="#556ee6" />
                                </td>
                              </tr>
                            ) : bookings.length === 0 ? (
                              <tr>
                                <td colSpan="10" className="text-center">
                                  No bookings found
                                </td>
                              </tr>
                            ) : (
                              bookings.map((booking, index) => (
                                <tr key={booking.booking_Id}>
                                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>{(page - 1) * pageSize + index + 1}</td>
                                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>{getCustomerName(booking.customer_Id)}</td>
                                  {/* Removed Employee, Room, From Time, To Time cells */}
                                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>{formatDate(booking.booking_Date)}</td>
                                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>{formatDate(booking.entry_Date)}</td>
                                  <td style={{ fontSize: '13px', verticalAlign: 'middle' }}>{getStatusBadge(booking)}</td>
                                  <td>
                                    <div className="d-flex gap-2 justify-content-start flex-wrap">
                                      <Button
                                        color="success"
                                        size="sm"
                                        onClick={() => handleExportDetails(booking.booking_Id)}
                                        disabled={loading}
                                        title="Export Details"
                                        style={{ fontSize: '12px', padding: '4px 8px', minWidth: 'auto' }}
                                      >
                                        <Download size={14} />
                                      </Button>
                                      <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => handleView(booking)}
                                        title="View Details"
                                        style={{ minWidth: '60px', fontSize: '12px' }}
                                      >
                                        View
                                      </Button>
                                      {hasEditOrHigher && (
                                        <Button
                                          color="warning"
                                          size="sm"
                                          onClick={() => handleEdit(booking)}
                                          disabled={booking.is_Cancelled}
                                          title="Edit Booking"
                                          style={{ minWidth: '60px', fontSize: '12px' }}
                                        >
                                          Edit
                                        </Button>
                                      )}
                                      {hasEditOrHigher && (
                                        !booking.invoice_Id ? (
                                          areAllServicesComplete(booking) ? (
                                            <Button
                                              color="success"
                                              size="sm"
                                              onClick={() => handlePayBill(booking)}
                                              disabled={booking.is_Cancelled}
                                              title="Pay Bill"
                                              style={{ minWidth: '60px', fontSize: '12px' }}
                                            >
                                              Bill
                                            </Button>
                                          ) : (
                                            <Button
                                              color="warning"
                                              size="sm"
                                              disabled={true}
                                              title="Pending - Complete all services first"
                                              style={{ minWidth: '60px', fontSize: '12px' }}
                                            >
                                              Pending
                                            </Button>
                                          )
                                        ) : (
                                          <Button
                                            color="info"
                                            size="sm"
                                            onClick={() => handleViewBill(booking)}
                                            title="View Bill"
                                            style={{ minWidth: '80px', fontSize: '12px' }}
                                          >
                                            View Bill
                                          </Button>
                                        )
                                      )}
                                      {hasDeletePermission && !booking.is_Cancelled && !areAllServicesComplete(booking) && (
                                        <Button
                                          color="danger"
                                          size="sm"
                                          onClick={() => handleCancel(booking.booking_Id)}
                                          title="Cancel Booking"
                                          style={{ minWidth: '70px', fontSize: '12px' }}
                                        >
                                          Cancel
                                        </Button>
                                      )}
                                      {hasEditOrHigher && !booking.is_Cancelled && booking.details && booking.details.length > 0 && (
                                        <Button
                                          color={checkIfAnyServicePending(booking) ? "success" : "warning"}
                                          size="sm"
                                          onClick={() => handleBulkCompletionStatusUpdate(booking)}
                                          title={
                                            !checkIfAllServicesHaveEmployees(booking) 
                                              ? "Assign employee for all services" 
                                              : checkIfAnyServicePending(booking) 
                                                ? "Mark all services as complete" 
                                                : "Mark all services as pending"
                                          }
                                          disabled={!checkIfAllServicesHaveEmployees(booking)}
                                          style={{ 
                                            minWidth: '80px', 
                                            fontSize: '12px',
                                            opacity: !checkIfAllServicesHaveEmployees(booking) ? 0.5 : 1
                                          }}
                                        >
                                          {checkIfAnyServicePending(booking) ? "Done" : "Mark Not Done"}
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>

                      {totalCount > 0 && <Pagination />}
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Cancellation Modal */}
      <Modal isOpen={showCancelModal} toggle={() => setShowCancelModal(false)}>
        <ModalHeader toggle={() => setShowCancelModal(false)}>
          Cancel Booking
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="cancelReason" className="mb-2">Cancellation Reason *</Label>
            <Input
              type="textarea"
              id="cancelReason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows="3"
              placeholder="Please provide a reason for cancellation..."
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowCancelModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmCancel} disabled={loading}>
            {loading ? <ClipLoader size={16} color="#fff" /> : "Confirm Cancellation"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Entity Cancellation Modal */}
      <Modal isOpen={showEntityCancelModal} toggle={() => setShowEntityCancelModal(false)}>
        <ModalHeader toggle={() => setShowEntityCancelModal(false)}>
          Cancel {cancellingEntity.name}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="entityCancelReason" className="mb-2">Cancellation Reason *</Label>
            <Input
              type="textarea"
              id="entityCancelReason"
              value={entityCancelReason}
              onChange={(e) => setEntityCancelReason(e.target.value)}
              rows="3"
              placeholder="Please provide a reason for cancelling this item..."
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowEntityCancelModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmEntityCancel}>
            Confirm Cancellation
          </Button>
        </ModalFooter>
      </Modal>

      {/* Membership Selection Modal */}
      <Modal isOpen={showMembershipModal} toggle={closeMembershipModal} size="xl">
        <ModalHeader toggle={closeMembershipModal}>
          <i className="mdi mdi-crown me-2"></i>
          Select Membership Items
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <p className="text-muted">
              Select services, packages, and package series from your membership. 
              You can select individual items or entire groups by checking the parent items.
            </p>
          </div>
          
          <div style={{ maxHeight: '500px', overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: '6px', padding: '15px' }}>
            {hierarchicalData.packageSeries.length > 0 ? (
              hierarchicalData.packageSeries.map((series) => (
                <div key={series.value} className="mb-4">
                  {/* Package Series Level */}
                  <div className="d-flex align-items-center mb-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`series-${series.value}`}
                        checked={hierarchicalSelection.packageSeries[series.value] || false}
                        onChange={(e) => handleHierarchicalSelection('packageSeries', series.value, e.target.checked)}
                      />
                      <label className="form-check-label fw-bold text-primary" htmlFor={`series-${series.value}`}>
                        <i className="mdi mdi-package-variant me-2"></i>
                        {series.label}
                      </label>
                    </div>
                  </div>
                  
                  {/* Packages Level */}
                  {series.packages && series.packages.length > 0 && (
                    <div className="ms-4">
                      {series.packages.map((pkg) => (
                        <div key={pkg.value} className="mb-3">
                          <div className="d-flex align-items-center mb-2">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`package-${pkg.value}`}
                                checked={hierarchicalSelection.packages[pkg.value] || false}
                                onChange={(e) => handleHierarchicalSelection('package', pkg.value, e.target.checked, series.value)}
                              />
                              <label className="form-check-label fw-semibold text-secondary" htmlFor={`package-${pkg.value}`}>
                                <i className="mdi mdi-package me-2"></i>
                                {pkg.label}
                              </label>
                            </div>
                          </div>
                          
                          {/* Services Level */}
                          {pkg.services && pkg.services.length > 0 && (
                            <div className="ms-4">
                              {pkg.services.map((service) => (
                                <div key={service.value} className="mb-1">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`service-${service.value}`}
                                      checked={hierarchicalSelection.services[service.value] || false}
                                      onChange={(e) => handleHierarchicalSelection('service', service.value, e.target.checked, pkg.value)}
                                    />
                                    <label className="form-check-label text-muted" htmlFor={`service-${service.value}`}>
                                      <i className="mdi mdi-spa me-2"></i>
                                      {service.label}
                                    </label>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center text-muted py-4">
                <i className="mdi mdi-information-outline me-2"></i>
                No membership items available for selection.
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeMembershipModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={applyMembershipSelections}>
            Apply Selections
          </Button>
        </ModalFooter>
      </Modal>

      {/* Quick Add Customer Modal */}
      <QuickAddCustomer
        isOpen={showQuickAddCustomer}
        toggle={() => setShowQuickAddCustomer(false)}
        onCustomerAdded={handleCustomerAdded}
      />
    </React.Fragment>
  );
};

export default BookingPage;
